import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Download } from "lucide-react";

import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Card, CardContent } from "../../components/Card";
import { DataTable } from "../../components/DataTable";
import SuccessPopup from "../../components/SuccessPopup";
import { IncrementalExportModal } from "../../components/IncrementalExportModal";
import {
  CSV_EXPORTS_QUERY_KEY,
  EXPORT_DATASETS,
  formatExportDate,
  getExportsSummary,
} from "../../services/csvExportService";

export function CsvExportsTab({ enabled = true }) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [popup, setPopup] = useState({ open: false, message: "", error: false });

  const { data, isLoading } = useQuery({
    queryKey: CSV_EXPORTS_QUERY_KEY,
    queryFn: getExportsSummary,
    enabled,
    staleTime: 0,
  });

  const datasets = data?.datasets || [];
  const selectedSummary = datasets.find((item) => item.dataset === selectedDataset);

  const handleExported = (count) => {
    queryClient.invalidateQueries({ queryKey: CSV_EXPORTS_QUERY_KEY });
    setPopup({
      open: true,
      message: count ? `CSV descargado: ${count} registros` : "No hay registros nuevos que descargar",
      error: !count,
    });
  };

  const secondaryText = { color: theme.palette.text.secondary, fontSize: "0.8rem" };

  return (
    <Card sx={{ width: "100%", maxWidth: "1100px" }}>
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mb: 0.5 }}>
              Descargas CSV
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Cada descarga queda registrada. La siguiente puede incluir solo lo creado desde entonces, o el
              listado completo.
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <DataTable
              variant="plain"
              size="sm"
              tableMinWidth={720}
              rows={datasets}
              getRowId={(item) => item.dataset}
              columns={[
                {
                  key: "dataset",
                  header: "Listado",
                  minWidth: "220px",
                  render: (item) => (
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{EXPORT_DATASETS[item.dataset]?.label}</Typography>
                      <Typography sx={secondaryText}>{EXPORT_DATASETS[item.dataset]?.description}</Typography>
                    </Box>
                  ),
                },
                {
                  key: "last_export",
                  header: "Última descarga",
                  minWidth: "180px",
                  render: (item) =>
                    item.last_export ? (
                      <Box>
                        <Typography sx={{ fontSize: "0.85rem" }}>
                          {formatExportDate(item.last_export.exported_at)}
                        </Typography>
                        <Typography sx={secondaryText}>{item.last_export.exported_by_name || "-"}</Typography>
                      </Box>
                    ) : (
                      <Typography sx={secondaryText}>Nunca</Typography>
                    ),
                },
                {
                  key: "pending_count",
                  header: "Nuevos",
                  minWidth: "120px",
                  render: (item) => (
                    <Badge color={item.pending_count > 0 ? "orange" : "gray"} dot={false}>
                      {item.last_export ? item.pending_count : `${item.total_count} (todo)`}
                    </Badge>
                  ),
                },
                {
                  key: "actions",
                  header: "",
                  minWidth: "140px",
                  render: (item) => (
                    <Button variant="outline" size="sm" onClick={() => setSelectedDataset(item.dataset)}>
                      <Download size={14} />
                      Descargar
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </Box>
      </CardContent>

      <IncrementalExportModal
        open={Boolean(selectedSummary)}
        summary={selectedSummary}
        onClose={() => setSelectedDataset(null)}
        onExported={handleExported}
      />
      <SuccessPopup
        message={popup.message}
        isOpen={popup.open}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
        autoHideDuration={3000}
        error={popup.error}
      />
    </Card>
  );
}
